import React, { useCallback, useEffect, useState } from 'react';
import {
  fetchUnmappedAccounts,
  setAccountPricing,
  UnmappedAccount,
  UnmappedAccountsResponse,
  PricingModelOptionResponse,
} from '../../lib/api';
import { modelHasValue } from '../../utils/pricing';
import { formatAccountName } from '../../utils/formatters';
import './NewAccounts.css';

interface NewAccountsProps {
  pricingModelOptions: PricingModelOptionResponse[];
  onClose: () => void;
}

interface RowState {
  modelType: string;
  modelValue: string;
  saving: boolean;
  saved: boolean;
  error: string | null;
}

const DEFAULT_MODEL = 'roi_guarantee';

export const NewAccounts: React.FC<NewAccountsProps> = ({
  pricingModelOptions,
  onClose,
}) => {
  const [data, setData] = useState<UnmappedAccountsResponse | null>(null);
  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showActivity, setShowActivity] = useState(false);

  const defaultValueFor = useCallback(
    (modelType: string): string => {
      const option = pricingModelOptions.find(o => o.model_type === modelType);
      return option ? String(option.default_value) : '';
    },
    [pricingModelOptions]
  );

  const load = useCallback(
    async (options?: { refresh?: boolean; activity?: boolean }) => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchUnmappedAccounts(options);
        setData(result);
        // Preserve any in-progress edits so a refresh doesn't wipe typed values.
        setRows(prev => {
          const next: Record<string, RowState> = {};
          result.accounts.forEach(({ account }) => {
            next[account] = prev[account] || {
              modelType: DEFAULT_MODEL,
              modelValue: defaultValueFor(DEFAULT_MODEL),
              saving: false,
              saved: false,
              error: null,
            };
          });
          return next;
        });
      } catch (e: any) {
        setError(e.message || 'Failed to load new accounts.');
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [defaultValueFor]
  );

  useEffect(() => {
    load();
  }, [load]);

  const updateRow = (account: string, patch: Partial<RowState>) => {
    setRows(prev => ({ ...prev, [account]: { ...prev[account], ...patch } }));
  };

  const handleModelChange = (account: string, modelType: string) => {
    updateRow(account, {
      modelType,
      modelValue: defaultValueFor(modelType),
      saved: false,
      error: null,
    });
  };

  const handleSave = async (account: string) => {
    const row = rows[account];
    if (!row) return;

    const value = Number(row.modelValue);
    if (row.modelValue.trim() === '' || Number.isNaN(value)) {
      updateRow(account, { error: 'Enter a number.' });
      return;
    }
    if (value < 0) {
      updateRow(account, { error: 'Value cannot be negative.' });
      return;
    }

    updateRow(account, { saving: true, error: null });
    try {
      await setAccountPricing({
        account,
        model_type: row.modelType,
        model_value: value,
      });
      updateRow(account, { saving: false, saved: true });
    } catch (e: any) {
      updateRow(account, {
        saving: false,
        error: e.message || 'Could not save pricing.',
      });
    }
  };

  // Newest first -- the recent additions are the ones whose pricing still
  // needs checking. Accounts with no known creation date sort to the bottom.
  const sortedAccounts = React.useMemo(() => {
    if (!data) return [];
    return [...data.accounts].sort((a, b) => {
      if (!a.created_date && !b.created_date) return 0;
      if (!a.created_date) return 1;
      if (!b.created_date) return -1;
      return b.created_date.localeCompare(a.created_date);
    });
  }, [data]);

  const savedCount = Object.values(rows).filter(r => r.saved).length;
  const remaining = (data?.unmapped_count || 0) - savedCount;

  return (
    <div className="new-accounts">
      <div className="new-accounts-header">
        <h2>New Accounts</h2>
        <button className="btn btn-secondary" onClick={onClose}>
          Back to Dashboard
        </button>
      </div>

      <p className="new-accounts-help">
        Accounts in Switchboard that aren&apos;t on the dashboard yet. They stay
        hidden until you set a price. Once saved, an account appears on the
        dashboard automatically.
      </p>

      <div className="new-accounts-toolbar">
        <button
          className="btn btn-secondary"
          onClick={() => load({ refresh: true, activity: showActivity })}
          disabled={loading}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
        <label className="new-accounts-toggle">
          <input
            type="checkbox"
            checked={showActivity}
            onChange={e => {
              const next = e.target.checked;
              setShowActivity(next);
              if (next) load({ activity: true });
            }}
            disabled={loading}
          />
          Show activity (slower)
        </label>
        {data && (
          <span className="new-accounts-counts">
            {remaining} unmapped &middot; {data.priced_account_count} priced &middot;{' '}
            {data.mirror_dataset_count} in mirror
          </span>
        )}
      </div>

      {error && <div className="new-accounts-error">{error}</div>}

      {!error && data && data.accounts.length === 0 && (
        <div className="new-accounts-empty">
          Every account in the mirror has pricing set. Nothing to add.
        </div>
      )}

      {!error && data && data.accounts.length > 0 && (
        <table className="new-accounts-table">
          <thead>
            <tr>
              <th>Account</th>
              <th>Account Created</th>
              {showActivity && <th>Last Sent</th>}
              {showActivity && <th className="num">Broadcasts</th>}
              <th>Pricing Model</th>
              <th className="num">Value</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sortedAccounts.map((account: UnmappedAccount) => {
              const row = rows[account.account];
              if (!row) return null;
              return (
                <tr
                  key={account.account}
                  className={row.saved ? 'is-saved' : undefined}
                >
                  <td>
                    <span className="new-accounts-name">
                      {formatAccountName(account.account)}
                    </span>
                    <code className="new-accounts-dataset">{account.account}</code>
                  </td>
                  <td className="new-accounts-created">
                    {account.created_date || <span className="muted">—</span>}
                  </td>
                  {showActivity && (
                    <td>{account.last_sent_date || <span className="muted">—</span>}</td>
                  )}
                  {showActivity && (
                    <td className="num">{account.broadcast_count ?? 0}</td>
                  )}
                  <td>
                    <select
                      value={row.modelType}
                      onChange={e => handleModelChange(account.account, e.target.value)}
                      disabled={row.saving || row.saved}
                    >
                      {pricingModelOptions.map(option => (
                        <option key={option.model_type} value={option.model_type}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="num">
                    <input
                      type="number"
                      step="any"
                      min="0"
                      value={row.modelValue}
                      onChange={e =>
                        updateRow(account.account, {
                          modelValue: e.target.value,
                          saved: false,
                          error: null,
                        })
                      }
                      disabled={row.saving || row.saved || !modelHasValue(row.modelType)}
                    />
                  </td>
                  <td>
                    {row.saved ? (
                      <span className="new-accounts-saved">Added</span>
                    ) : (
                      <button
                        className="btn btn-primary"
                        onClick={() => handleSave(account.account)}
                        disabled={row.saving}
                      >
                        {row.saving ? 'Saving…' : 'Add'}
                      </button>
                    )}
                    {row.error && (
                      <div className="new-accounts-row-error">{row.error}</div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {savedCount > 0 && (
        <p className="new-accounts-footnote">
          {savedCount} account{savedCount === 1 ? '' : 's'} added. Reload the
          dashboard to see {savedCount === 1 ? 'it' : 'them'} in the account list.
        </p>
      )}
    </div>
  );
};
